import { useCallback, useRef } from 'react'
import { defineSteps, step } from 'flows/common/helpers/defineSteps'
import { BrandPlanAwarenessStep } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/BrandPlanAwarenessStep'
import { BrandPlanAwarenessTerminalStep } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/BrandPlanAwarenessTerminalStep'
import { BrandPlanTactics } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { BrandProfileStep } from 'components/FirstTimeExperienceModal/steps/BrandProfile/BrandProfileStep'
import { BrandKitConfirm } from 'screens/VirtualMarketerOnboarding/BrandKitConfirm/BrandKitConfirm'
import { BrandProfileWebsite } from 'screens/VirtualMarketerOnboarding/BrandProfileWebsite/BrandProfileWebsite'
import { EnhanceBrandColors } from 'screens/VirtualMarketerOnboarding/EnhanceBrandColors/EnhanceBrandColors'
import { EnhanceBrandFonts } from 'screens/VirtualMarketerOnboarding/EnhanceBrandFonts/EnhanceBrandFonts'
import { ContentGallery } from 'screens/VirtualMarketerOnboarding/ContentGallery/ContentGallery'

import { FlowCtx } from 'flows/common/machines/createFlowMachine'
import { StepProps } from 'flows/virtualMarketerOnboarding/Types'
import { CampaignGeneration } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/CampaignGeneration'
import { CampaignDetails } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/CampaignDetails'
import {
  CampaignGenerationLoadingScreen,
  CampaignGenerationLoadingProvider,
} from 'components/FirstTimeExperienceModal/steps/CampaignGeneration'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useDeleteBrandProfile } from 'hooks/queries/brandProfiles/useDeleteBrandProfile'
import { AutopilotSidebar as ProfileAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/BrandProfile/AutopilotSidebar'
import { AutopilotSidebar as FontsAutopilotSidebar } from 'screens/VirtualMarketerOnboarding/EnhanceBrandFonts/AutopilotSidebar'
import { AutopilotSidebar as ColorsAutopilotSidebar } from 'screens/VirtualMarketerOnboarding/EnhanceBrandColors/AutopilotSidebar'
import { AutopilotSidebar as ConfirmAutopilotSidebar } from 'screens/VirtualMarketerOnboarding/BrandKitConfirm/AutopilotSidebar'
import { AutopilotSidebar as TacticsAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/AutopilotSidebar'
import { AutopilotSidebar as CurrentVolumeAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/BrandPlanCurrentVolume/AutopilotSidebar'
import { AutopilotSidebar as AwarenessAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/AutopilotSidebar'
import { AutopilotSidebar as ScreenshotFallbackSidebar } from 'screens/VirtualMarketerOnboarding/ScreenshotFallback/AutopilotSidebar'
import { AutopilotSidebar as FollowerGrowthAutopilotSidebar } from 'screens/VirtualMarketerOnboarding/FollowerGrowthExplainer/FollowerGrowthExplainer'
import { AutopilotSidebar as CampaignGenerationAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/AutopilotSidebar'
import { useExperiment } from 'utils/experiments'
import { BrandPlanCurrentVolume } from 'components/FirstTimeExperienceModal/steps/BrandPlanCurrentVolume/BrandPlanCurrentVolume'
import { ScreenshotFallback } from 'screens/VirtualMarketerOnboarding/ScreenshotFallback/ScreenshotFallback'
import AutopilotRegistrationAndOnboardingStyles from 'screens/AutopilotRegistration/AutopilotRegistrationAndOnboarding.module.scss'
import { MobilePushToApp } from 'screens/VirtualMarketerOnboarding/MobilePushToApp/MobilePushToApp'
import { VIRTUAL_MARKETER_ONBOARDING_PATH } from 'paths'
import { generatePath } from 'react-router-dom'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { NoWebsite } from 'screens/VirtualMarketerOnboarding/NoWebsite/NoWebsite'
import { BlazeExplainer } from 'screens/VirtualMarketerOnboarding/BlazeExplainer/BlazeExplainer'
import { FollowerGrowthExplainer } from 'screens/VirtualMarketerOnboarding/FollowerGrowthExplainer/FollowerGrowthExplainer'
import { BrandVisualStyleSelection } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection'
import { BrandVisualStylePreview } from 'screens/VirtualMarketerOnboarding/BrandVisualStylePreview'
import { useCampaignsFeatureFlag } from 'hooks/useCampaignsFeatureFlag'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { useIsOnboardingRedesignV2 } from 'hooks/useIsOnboardingRedesignV2'
import { BrandVisualStyleFonts } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleFonts/BrandVisualStyleFonts'
import { BrandVisualStyleAnimatedImages } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleAnimatedImages/BrandVisualStyleAnimatedImages'
import { BrandVisualStyleOptIn } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleOptIn/BrandVisualStyleOptIn'
import { BrandVisualStyleMedia } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleMedia/BrandVisualStyleMedia'
import { useIsVisualStylesOnboarding } from 'hooks/useIsVisualStylesOnboarding'
import { StarterOrPioneerVariant, useIsPioneerStarterOrGrowth } from 'hooks/useIsPioneerStarterOrGrowth'
import { useChooseOnboardingPlan } from './useChooseOnboardingPlan'
import { PerformanceInsights } from 'screens/VirtualMarketerOnboarding/PerformanceInsights/PerformanceInsights'
import { InsightChannelsSelection } from 'screens/VirtualMarketerOnboarding/InsightChannelsSelection/InsightChannelsSelection'
import { StrategicRecommendations } from 'screens/VirtualMarketerOnboarding/StrategicRecommendations/StrategicRecommendations'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const useContentGallerySkipped = () => {
  const experimentVariant = useExperiment('content_gallery')
  const { isMobile } = useResponsiveDesign()
  return isMobile || experimentVariant === 'skip_gallery'
}

const useBlazeExplainerSkipped = () => {
  const experimentVariant = useExperiment('blaze_explainer')
  const { isMobile } = useResponsiveDesign()
  return isMobile || experimentVariant === 'skip_explainer'
}

const useGrowthGraphSkipped = () => {
  const experimentVariant = useExperiment('growth_graph')
  const { isMobile } = useResponsiveDesign()
  return isMobile || experimentVariant === 'control'
}

const isStarterFlow = (context: FlowCtx, planVariant: StarterOrPioneerVariant) => {
  return (
    context.preSelectedPlan === 'starter' ||
    context.campaignData?.selectedPlan === 'starter' ||
    context.selectedPlan === 'starter' ||
    planVariant === 'starter'
  )
}

const useStableBackHandler = <T extends (...args: any[]) => void>(
  handler: T,
  onProvideBackHandler?: (handler: (() => void) | null) => void,
) => {
  const handleBackRef = useRef<T>()
  const stableHandlerRef = useRef<() => void>()

  handleBackRef.current = handler

  if (!stableHandlerRef.current) {
    stableHandlerRef.current = () => {
      if (handleBackRef.current) {
        handleBackRef.current()
      }
    }
  }

  if (onProvideBackHandler && stableHandlerRef.current) {
    onProvideBackHandler(stableHandlerRef.current)
  }

  return handler
}

const BrandProfileWebsiteWrapper = ({ send, context }: any) => {
  let effectiveBrandKitId = context.brandKitId
  if (!context.isIntentionalBrandKit) {
    effectiveBrandKitId = null
  }

  return (
    <BrandProfileWebsite
      websites={context.websites}
      language={context.language || context.defaultLanguage}
      brandKitId={effectiveBrandKitId}
      isPlanPreSelected={context.isPlanPreSelected}
      preSelectedPlan={context.preSelectedPlan}
      onLanguageChange={(language) => {
        send({ type: 'MERGE_CTX', payload: { language } })
      }}
      onBack={() => send({ type: 'BACK' })}
      onNext={async (brandProfile, brandVisualEnhancementId) => {
        let analysisId = brandProfile.latest_analysis_id

        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId,
            brandVisualEnhancementId,
            websiteScrapeSucceeded: true,
          },
        })
      }}
      onNoWebsite={() => {
        send({ type: 'NO_WEBSITE' })
      }}
    />
  )
}

const ScreenshotFallbackWrapper = ({
  send,
  context,
  onProvideBackHandler,
}: StepProps & { onProvideBackHandler?: (handler: (() => void) | null) => void }) => {
  const handleBack = useCallback(() => {
    send({ type: 'BACK' })
  }, [send])

  useStableBackHandler(handleBack, onProvideBackHandler)

  return (
    <ScreenshotFallback
      onBack={handleBack}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
            websiteScrapeSucceeded: true,
          },
        })
      }}
    />
  )
}

const NoWebsiteWrapper = ({
  send,
  context,
  onProvideBackHandler,
}: StepProps & { onProvideBackHandler?: (handler: (() => void) | null) => void }) => {
  const handleBack = useCallback(() => {
    send({ type: 'BACK' })
  }, [send])

  useStableBackHandler(handleBack, onProvideBackHandler)

  return (
    <NoWebsite
      onBack={handleBack}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
          },
        })
      }}
    />
  )
}

const BrandProfileStepWrapper = ({
  send,
  context,
  onProvideBackHandler,
}: StepProps & { onProvideBackHandler?: (handler: (() => void) | null) => void }) => {
  const { data: brandKitData } = useBrandKit(context.brandKitId, { cacheTime: 1000 * 60 * 5 })
  const { mutate: deleteBrandProfile } = useDeleteBrandProfile({ id: context.brandProfileId })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource()

  const handleBack = useCallback(
    (websiteScrapeSucceeded = true) => {
      deleteBrandProfile()
      const brandKitSources = brandKitData?.brand_kit?.brand_kit_sources
      brandKitSources?.forEach((source) => {
        context.brandKitId && deleteBrandKitSource({ brandKitId: context.brandKitId, id: source.id })
      })

      send({
        type: 'MERGE_CTX',
        payload: {
          brandProfileId: null,
          brandProfile: null,
          skipUploadBrandMaterials: false,
          brandKitId: context.isIntentionalBrandKit ? context.brandKitId : null,
          isIntentionalBrandKit: context.isIntentionalBrandKit || false,
          profile: null,
          websiteScrapeSucceeded,
        },
      })

      send({ type: 'BACK' })
    },
    [
      deleteBrandProfile,
      brandKitData?.brand_kit?.brand_kit_sources,
      context.brandKitId,
      context.isIntentionalBrandKit,
      deleteBrandKitSource,
      send,
    ],
  )

  useStableBackHandler(handleBack, onProvideBackHandler)

  const { saveSubscriptionPlan } = useChooseOnboardingPlan({
    onPlanSet: (selectedPlan) => {
      if (selectedPlan === 'starter') {
        send({
          type: 'CHOOSE_STARTER',
          payload: { selectedPlan, strategyType: 'EvergreenStrategy' },
        })
      } else {
        send({
          type: 'CHOOSE_GROWTH',
          payload: { selectedPlan },
        })
      }
    },
  })

  return (
    <BrandProfileStep
      analysisId={context.analysisId}
      id={context.brandProfileId}
      initialProfile={context.profile}
      deleteOnBack={false}
      onAnalysis={(analysisId) => {
        send({ type: 'MERGE_CTX', payload: { analysisId } })
      }}
      onNext={(profile) => {
        // In the redesigned flow there is no screen to choose the plan, so we set it here as
        // its one of the first steps available and common to all onboarding flows
        saveSubscriptionPlan()

        send({
          type: 'MERGE_CTX',
          payload: { profile },
        })
        if (context.isMobile) {
          send({ type: 'IS_MOBILE_NEXT' })
        } else {
          send({ type: 'NEXT' })
        }
      }}
      onBack={handleBack}
    />
  )
}

const EnhanceBrandFontsWrapper = ({
  send,
  context,
  onProvideBackHandler,
}: StepProps & { onProvideBackHandler?: (handler: (() => void) | null) => void }) => {
  if (!context.brandKitId) {
    throw new Error('Brand kit ID is required for EnhanceBrandFonts')
  }

  const handleBack = useCallback(() => {
    if (context.fromBrandKitConfirm) {
      send({ type: 'BACK_FROM_EDIT', payload: { isReturningFromEdit: true } })
    } else {
      send({ type: 'BACK' })
    }
  }, [context.fromBrandKitConfirm, send])

  useStableBackHandler(handleBack, onProvideBackHandler)

  return (
    <EnhanceBrandFonts
      onNext={({ isUsingEnhancedFonts, selectedEnhancedFontIndex }) => {
        send({
          type: context.fromBrandKitConfirm ? 'NEXT_FROM_EDIT' : 'NEXT',
          payload: {
            isUsingEnhancedFonts,
            selectedEnhancedFontIndex,
            isReturningFromEdit: context.fromBrandKitConfirm,
          },
        })
      }}
      onBack={handleBack}
      brandKitId={context.brandKitId}
      brandVisualEnhancementId={context.brandVisualEnhancementId}
      selectedEnhancedFontIndex={context.selectedEnhancedFontIndex}
      fromBrandKitConfirm={context.fromBrandKitConfirm}
      brandKit={context.brandKit}
      enhancement={context.enhancement}
      onEnhancementCreated={(brandVisualEnhancementId) => {
        send({ type: 'MERGE_CTX', payload: { brandVisualEnhancementId } })
      }}
    />
  )
}

const EnhanceBrandColorsWrapper = ({
  send,
  context,
  onProvideBackHandler,
}: StepProps & { onProvideBackHandler?: (handler: (() => void) | null) => void }) => {
  if (!context.brandKitId) {
    throw new Error('Brand kit ID is required for EnhanceBrandColors')
  }

  const handleBack = useCallback(() => {
    if (context.fromBrandKitConfirm) {
      send({ type: 'BACK_FROM_EDIT', payload: { isReturningFromEdit: true } })
    } else {
      send({ type: 'BACK' })
    }
  }, [context.fromBrandKitConfirm, send])

  useStableBackHandler(handleBack, onProvideBackHandler)

  return (
    <EnhanceBrandColors
      onNext={({ isUsingEnhancedColors, selectedEnhancedColorIndex }) => {
        send({
          type: context.fromBrandKitConfirm ? 'NEXT_FROM_EDIT' : 'NEXT',
          payload: {
            isUsingEnhancedColors,
            selectedEnhancedColorIndex,
            isReturningFromEdit: context.fromBrandKitConfirm,
          },
        })
      }}
      onBack={handleBack}
      brandKitId={context.brandKitId}
      brandVisualEnhancementId={context.brandVisualEnhancementId}
      selectedEnhancedColorIndex={context.selectedEnhancedColorIndex}
      fromBrandKitConfirm={context.fromBrandKitConfirm}
      brandKit={context.brandKit}
      enhancement={context.enhancement}
    />
  )
}

const BrandKitConfirmWrapper = ({
  send,
  context,
  onProvideBackHandler,
}: StepProps & { onProvideBackHandler?: (handler: (() => void) | null) => void }) => {
  if (!context.brandKitId) {
    throw new Error('Brand kit ID is required for BrandKitConfirm')
  }

  const handleBack = useCallback(() => {
    send({ type: 'BACK', payload: { fromBrandKitConfirm: false } })
  }, [send])

  useStableBackHandler(handleBack, onProvideBackHandler)

  return (
    <BrandKitConfirm
      onNext={() => {
        send({ type: 'NEXT' })
      }}
      onBack={handleBack}
      brandKitId={context.brandKitId}
      isUsingEnhancedFonts={context.isUsingEnhancedFonts}
      isUsingEnhancedColors={context.isUsingEnhancedColors}
      selectedEnhancedFontIndex={context.selectedEnhancedFontIndex}
      selectedEnhancedColorIndex={context.selectedEnhancedColorIndex}
      brandVisualEnhancementId={context.brandVisualEnhancementId}
      onEnhancementCreated={(brandVisualEnhancementId) => {
        send({ type: 'MERGE_CTX', payload: { brandVisualEnhancementId } })
      }}
      onNavigateToFonts={(brandKit, enhancement) =>
        send({ type: 'NAVIGATE_TO_FONTS', payload: { fromBrandKitConfirm: true, brandKit, enhancement } })
      }
      onNavigateToColors={(brandKit, enhancement) =>
        send({ type: 'NAVIGATE_TO_COLORS', payload: { fromBrandKitConfirm: true, brandKit, enhancement } })
      }
      brandProfileId={context.brandProfileId}
      isReturningFromEdit={context.isReturningFromEdit}
    />
  )
}

const ContentGalleryWrapper = ({ send, context }: StepProps) => {
  const isContentGallerySkipped = useContentGallerySkipped()

  if (isContentGallerySkipped) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <ContentGallery
      brandKitId={context.brandKitId}
      onNext={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK_FROM_EDIT', payload: { isReturningFromEdit: true } })}
      onBackToProfile={() => send({ type: 'BACK_TO_PROFILE' })}
    />
  )
}

const BrandPlanTacticsWrapper = ({ send, context }: StepProps) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const isContentGallerySkipped = useContentGallerySkipped()
  const isBlazeExplainerSkipped = useBlazeExplainerSkipped()
  const isGrowthGraphSkipped = useGrowthGraphSkipped()
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()
  const planVariant = useIsPioneerStarterOrGrowth()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for BrandPlanAwarenessStep')
  }

  const handleBack = () => {
    if (isOnboardingRedesign) {
      if (isVisualStylesEnabled) {
        if (context.visualStylesOptIn) {
          send({ type: 'BACK_TO_VISUAL_STYLES_IN' })
        } else {
          send({ type: 'BACK_TO_VISUAL_STYLES_OUT' })
        }
      } else {
        send({ type: 'BACK' })
      }
    } else if (isVisualStylesEnabled) {
      send({ type: 'BACK_TO_VISUAL_STYLES' })
    } else if (isContentGallerySkipped && isBlazeExplainerSkipped) {
      send({ type: 'BACK_TO_BRAND_KIT' })
    } else if (isBlazeExplainerSkipped && !isContentGallerySkipped) {
      send({ type: 'BACK_TO_CONTENT_GALLERY' })
    } else if (isGrowthGraphSkipped && !isBlazeExplainerSkipped) {
      send({ type: 'BACK_TO_BLAZE_EXPLAINER' })
    } else {
      send({ type: 'BACK' })
    }
  }

  const mode = isStarterFlow(context, planVariant) ? 'starter' : 'evergreen'

  return (
    <BrandPlanTactics
      externalIntegrations={context.externalIntegrations}
      brandKitId={context.brandKitId}
      id={context.brandPlanId}
      tactics={context.tactics}
      seoKeywords={context.seoKeywords}
      idealCustomers={context.idealCustomers}
      mode={mode}
      onBack={handleBack}
      initialInsightChannels={context.insightChannels || []}
      onNext={(data) => {
        send({
          type: 'NEXT',
          payload: {
            tactics: data.tactics,
            seoKeywords: data.seoKeywords,
            idealCustomers: data.idealCustomers,
            selectedBlogPlatform: data.selectedBlogPlatform,
            selectedEmailPlatform: data.selectedEmailPlatform,
            paidAdsOnboardingParameters: data.paidAdsOnboardingParameters,
          },
        })
      }}
    />
  )
}

const BrandPlanCurrentVolumeWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for BrandPlanCurrentVolume')
  }

  return (
    <BrandPlanCurrentVolume
      id={context.brandPlanId}
      selectedBlogPlatform={context.selectedBlogPlatform}
      selectedEmailPlatform={context.selectedEmailPlatform}
      onBack={() => send({ type: 'BACK' })}
      onNext={(payload) => send({ type: 'NEXT', payload })}
    />
  )
}

const MidFlowMobilePushToAppWrapper = ({ send, context }: StepProps) => {
  const experimentVariant = useExperiment('mid_flow_mobile')
  const { isIOSNative } = useResponsiveDesign()

  if (experimentVariant === 'skip_mobile' || isIOSNative) {
    send({ type: 'NEXT' })
    return null
  }

  const desktopPath = generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, { workspaceId: context.currentWorkspaceId })

  return <MobilePushToApp isMidFlow desktopPath={desktopPath} onContinue={() => send({ type: 'NEXT' })} />
}

export const BrandPlanAwarenessWrapper = ({ send, context }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for BrandPlanAwarenessStep')
  }

  const { isMobile } = useResponsiveDesign()
  const integrationsOnboardingVariant = useExperiment('integrations_onboarding')

  if (integrationsOnboardingVariant !== 'v2' || isMobile) {
    return (
      <BrandPlanAwarenessTerminalStep
        id={context.brandPlanId}
        selectedBlogPlatform={context.selectedBlogPlatform}
        selectedEmailPlatform={context.selectedEmailPlatform}
        isStarterPlan={isStarterFlow(context, planVariant)}
        paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
        onBack={() => send({ type: 'BACK' })}
        onNext={(payload) => {
          if (payload?.desktopPath && payload?.publishDay) {
            send({ type: 'MOBILE_COMPLETE', payload })
          } else {
            send({ type: 'NEXT', payload })
          }
        }}
      />
    )
  }

  return (
    <BrandPlanAwarenessStep
      id={context.brandPlanId}
      selectedBlogPlatform={context.selectedBlogPlatform}
      selectedEmailPlatform={context.selectedEmailPlatform}
      onBack={() => send({ type: 'BACK' })}
      onNext={() => send({ type: 'NEXT' })}
    />
  )
}

const WhereToPublishWrapper = ({ send, context }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()
  const isOnboardingBrainEnabled = useFlipperFlag('onboarding_brain') && planVariant === 'starter'
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for WhereToPublish')
  }

  const mode = isStarterFlow(context, planVariant) ? 'starter' : 'campaign'

  return (
    <BrandPlanTactics
      id={context.brandPlanId}
      mode={mode}
      externalIntegrations={context.externalIntegrations}
      brandKitId={context.brandKitId}
      tactics={context.tactics}
      seoKeywords={context.seoKeywords}
      idealCustomers={context.idealCustomers}
      initialInsightChannels={context.insightChannels || []}
      onBack={() => {
        if (!isOnboardingBrainEnabled) {
          if (isVisualStylesEnabled) {
            if (context.visualStylesOptIn) {
              send({ type: 'BACK_TO_VISUAL_STYLES_IN' })
            } else {
              send({ type: 'BACK_TO_VISUAL_STYLES_OUT' })
            }
          }

          send({ type: 'BACK_WITHOUT_BRAIN' })
          return
        }

        if (!context.withInsights) {
          send({ type: 'BACK_WITHOUT_INSIGHT_CHANNELS' })
        } else {
          send({ type: 'BACK' })
        }
      }}
      onNext={({ tactics, startDate, selectedBlogPlatform, selectedEmailPlatform, paidAdsOnboardingParameters }) => {
        const payload = { tactics, startDate, selectedBlogPlatform, selectedEmailPlatform, paidAdsOnboardingParameters }

        if (isStarterFlow(context, planVariant)) {
          send({
            type: 'NEXT_STARTER',
            payload,
          })
        } else {
          send({
            type: 'NEXT_GROWTH',
            payload,
          })
        }
      }}
    />
  )
}

const CampaignGenerationLoadingWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for CampaignGenerationLoading')
  }

  return (
    <CampaignGenerationLoadingProvider>
      <CampaignGenerationLoadingScreen
        brandPlanId={context.brandPlanId}
        campaignData={context.campaignData}
        startDate={context.startDate}
        onComplete={() => send({ type: 'COMPLETE' })}
        onBack={() => send({ type: 'BACK' })}
      />
    </CampaignGenerationLoadingProvider>
  )
}

const CampaignGenerationWrapper = ({ send, context }: StepProps) => {
  const isCampaignsEnabled = useCampaignsFeatureFlag()
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()

  if (!isCampaignsEnabled) {
    send({ type: 'SKIP_CAMPAIGN_GENERATION' })
    return null
  }

  if (!context.brandPlanId || !context.brandKitId) {
    throw new Error('Brand plan ID and brand kit ID are required for CampaignGeneration')
  }

  const handleBack = () => {
    if (isVisualStylesEnabled) {
      if (context.visualStylesOptIn) {
        send({ type: 'BACK_TO_VISUAL_STYLES' })
      } else {
        send({ type: 'BACK_TO_VISUAL_STYLES_MEDIA' })
      }
    } else {
      send({ type: 'BACK' })
    }
  }

  return (
    <CampaignGeneration
      brandPlanId={context.brandPlanId}
      brandKitId={context.brandKitId}
      initialValues={context.campaignData}
      skipSubscriptionUpdate={false}
      disablePlanChoice={false}
      onBack={handleBack}
      onNext={(campaignData) => {
        send({
          type: 'NEXT',
          payload: {
            campaignData: {
              growth_goals_input: campaignData.growth_goals_input,
              key_dates_input: campaignData.key_dates_input,
            },
            selectedPlan: campaignData.selectedPlan || 'growth',
          },
        })
      }}
    />
  )
}

const InsightChannelsWrapper = ({ send, context }: StepProps) => {
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()
  const planVariant = useIsPioneerStarterOrGrowth()
  const isOnboardingBrainEnabled = useFlipperFlag('onboarding_brain') && planVariant === 'starter'

  if (!isOnboardingBrainEnabled) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <InsightChannelsSelection
      initialInsightChannels={context.insightChannels || []}
      onBack={() => {
        if (isVisualStylesEnabled) {
          if (context.visualStylesOptIn) {
            send({ type: 'BACK_TO_VISUAL_STYLES_IN' })
          } else {
            send({ type: 'BACK_TO_VISUAL_STYLES_OUT' })
          }
        } else {
          send({ type: 'BACK' })
        }
      }}
      onNext={(insightChannels) => {
        send({ type: 'MERGE_CTX', payload: { insightChannels } })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const PerformanceInsightsWrapper = ({ send, context }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()
  const isOnboardingBrainEnabled = useFlipperFlag('onboarding_brain') && planVariant === 'starter'

  if (!isOnboardingBrainEnabled) {
    send({ type: 'NEXT' })
    return null
  }

  if (!context.brandKitId) {
    throw new Error('Brand kit ID is required for PerformanceInsights')
  }

  return (
    <PerformanceInsights
      insightChannels={context.insightChannels}
      brandKitId={context.brandKitId}
      onBack={() => {
        send({ type: 'MERGE_CTX', payload: { withInsights: false } })
        send({ type: 'BACK' })
      }}
      onNext={() => {
        send({ type: 'MERGE_CTX', payload: { withInsights: true } })
        send({ type: 'NEXT' })
      }}
      onSkip={() => send({ type: 'SKIP' })}
    />
  )
}

const StrategicRecommendationsWrapper = ({ send, context }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()
  const isOnboardingBrainEnabled = useFlipperFlag('onboarding_brain') && planVariant === 'starter'

  if (!isOnboardingBrainEnabled) {
    send({ type: 'NEXT' })
    return null
  }

  if (!context.brandKitId) {
    throw new Error('Brand kit ID is required for StrategicRecommendations')
  }

  return (
    <StrategicRecommendations
      brandKitId={context.brandKitId}
      onBack={() => send({ type: 'BACK' })}
      onNext={() => send({ type: 'NEXT' })}
    />
  )
}

const CampaignDetailsWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId || !context.brandKitId) {
    throw new Error('Brand plan ID and brand kit ID are required for CampaignDetails')
  }

  return (
    <CampaignDetails
      brandPlanId={context.brandPlanId}
      brandKitId={context.brandKitId}
      initialValues={context.campaignData}
      paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
      onBack={() => send({ type: 'BACK' })}
      onNext={(campaignData) => {
        send({
          type: 'NEXT',
          payload: {
            campaignData: {
              growth_goals_input: campaignData.growth_goals_input,
              key_dates_input: campaignData.key_dates_input,
            },
            startDate: campaignData.startDate,
          },
        })
      }}
    />
  )
}

const BlazeExplainerWrapper = ({ send }: StepProps) => {
  const isBlazeExplainerSkipped = useBlazeExplainerSkipped()
  const isContentGallerySkipped = useContentGallerySkipped()

  if (isBlazeExplainerSkipped) {
    send({ type: 'NEXT' })
    return null
  }

  const handleBack = () => {
    if (isContentGallerySkipped) {
      send({ type: 'BACK_TO_BRAND_KIT' })
    } else {
      send({ type: 'BACK' })
    }
  }

  return <BlazeExplainer onBack={handleBack} onNext={() => send({ type: 'NEXT' })} />
}

const FollowerGrowthExplainerWrapper = ({ send }: StepProps) => {
  const isGrowthGraphSkipped = useGrowthGraphSkipped()

  if (isGrowthGraphSkipped) {
    send({ type: 'NEXT' })
    return null
  }

  return <FollowerGrowthExplainer onBack={() => send({ type: 'BACK' })} onNext={() => send({ type: 'NEXT' })} />
}

const BrandVisualStyleSelectionWrapper = ({ send, context }: StepProps) => {
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()

  if (!isVisualStylesEnabled) {
    send({ type: 'SKIP_VISUAL_STYLES' })
    return null
  }

  return (
    <BrandVisualStyleSelection
      brandKitId={context.brandKitId}
      onBack={() => send({ type: 'BACK' })}
      onNext={(fontPairings, stylesGenerated, sourceImage) => {
        send({
          type: 'MERGE_CTX',
          payload: { fontPairings, stylesGenerated, sourceImage },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStylePreviewWrapper = ({ send, context }: StepProps) => {
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()

  // Safety check - shouldn't reach here if FF is disabled, but handle gracefully
  if (!isVisualStylesEnabled) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStylePreview
      onBack={() => {
        send({ type: 'BACK' })
      }}
      initialSelectedStyle={context.selectedVisualStyle}
      initialStylesGenerated={context.stylesGenerated}
      sourceImage={context.sourceImage}
      brandKitId={context.brandKitId}
      onNext={(selectedVisualStyle, stylesGenerated) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedVisualStyle, stylesGenerated },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleMediaWrapper = ({ send, context }: StepProps) => {
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()
  const isRedesignV2 = useIsOnboardingRedesignV2()

  // Safety check - shouldn't reach here if FF is disabled, but handle gracefully
  if (!isVisualStylesEnabled || !context.brandKitId) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleMedia
      onBack={() => {
        if (isRedesignV2) {
          send({ type: 'BACK' })
        } else {
          send({ type: 'BACK_TO_PREVIEW' })
        }
      }}
      brandKitId={context.brandKitId}
      forVisualStyles
      onNext={() => send({ type: 'NEXT' })}
    />
  )
}

const BrandVisualStyleFontsWrapper = ({ send, context }: StepProps) => {
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()

  // Safety check - shouldn't reach here if FF is disabled, but handle gracefully
  if (!isVisualStylesEnabled) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleFonts
      onBack={() => {
        send({ type: 'BACK' })
      }}
      brandKitId={context.brandKitId}
      initialFontPairings={context.fontPairings}
      initialFontPairingId={context.selectedFontPairingId}
      selectedStyle={context.selectedVisualStyle}
      onNext={(selectedFontPairingId) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedFontPairingId },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleOptInOrOutWrapper = ({ send, context }: StepProps) => {
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()
  const isVisualStylesOptInV2 = useFlipperFlag('visual_styles_optin_v2')

  // Safety check - shouldn't reach here if FF is disabled, but handle gracefully
  if (!isVisualStylesEnabled || !context.brandKitId) {
    send({ type: 'NEXT' })
    return null
  }

  if (!isVisualStylesOptInV2) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleOptIn
      onBack={() => {
        send({ type: 'BACK' })
      }}
      brandKitId={context.brandKitId}
      selectedStyle={context.selectedVisualStyle}
      onNext={(visualStylesOptIn) => {
        send({
          type: 'MERGE_CTX',
          payload: { visualStylesOptIn },
        })
        send({ type: !!visualStylesOptIn ? 'NEXT_IN' : 'NEXT_OUT' })
      }}
    />
  )
}

const BrandVisualStyleMediaOptOutWrapper = ({ send, context }: StepProps) => {
  const isVisualStylesEnabled = useIsVisualStylesOnboarding()

  // Safety check - shouldn't reach here if FF is disabled, but handle gracefully
  if (!isVisualStylesEnabled || !context.brandKitId) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleMedia
      onBack={() => {
        send({ type: 'BACK' })
      }}
      brandKitId={context.brandKitId}
      forVisualStyles
      onNext={(visualStylesOptIn) => {
        send({
          type: 'MERGE_CTX',
          payload: { visualStylesOptIn },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleAnimatedImagesWrapper = ({ send, context }: StepProps) => {
  const isOnboardingRedesignV2 = useIsOnboardingRedesignV2()
  if (!isOnboardingRedesignV2 || !context.brandKitId) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleAnimatedImages
      onNext={(optedIn) => {
        send({
          type: 'MERGE_CTX',
          payload: { animatedImagesOptIn: optedIn },
        })
        send({ type: 'NEXT' })
      }}
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
    />
  )
}

const flowStep = step<FlowCtx>

export const steps = [
  flowStep('screenshotFallback', ScreenshotFallbackWrapper)
    .withSiderComponent(ScreenshotFallbackSidebar)
    .skipWhen((ctx) => ctx.websiteScrapeSucceeded)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .back('website'),
  flowStep('noWebsite', NoWebsiteWrapper)
    .back('website')
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),
  flowStep('profile', BrandProfileStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website', (ctx) => ctx.websiteScrapeSucceeded)
    .back('screenshotFallback', (ctx) => !ctx.websiteScrapeSucceeded)
    .on('IS_MOBILE_NEXT', 'midFlowMobilePushToApp')
    .on('NEXT', 'brandVisualStyleSelection')
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),
  flowStep('midFlowMobilePushToApp', MidFlowMobilePushToAppWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .on('NEXT', 'brandVisualStyleSelection')
    .skipWhen((ctx) => !ctx.isMobile),
  flowStep('brandVisualStyleSelection', BrandVisualStyleSelectionWrapper)
    .back('profile')
    .on('SKIP_VISUAL_STYLES', 'brandKitConfirm')
    .on('NEXT', 'brandVisualStylePreview')
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),
  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .back('brandVisualStyleSelection')
    .on('NEXT', 'brandVisualStyleOptInOrOut'),
  flowStep('brandVisualStyleOptInOrOut', BrandVisualStyleOptInOrOutWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .back('brandVisualStylePreview')
    .on('NEXT_OUT', 'brandVisualStyleMediaOptOut')
    .on('NEXT_IN', 'brandVisualStyleMedia'),

  flowStep('brandVisualStyleMedia', BrandVisualStyleMediaWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .back('brandVisualStyleOptInOrOut')
    .on('BACK_TO_PREVIEW', 'brandVisualStylePreview')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .back('brandVisualStyleMedia')
    .on('NEXT', 'campaignGeneration'),
  flowStep('brandVisualStyleMediaOptOut', BrandVisualStyleMediaOptOutWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .back('brandVisualStyleOptInOrOut')
    .on('NEXT', 'campaignGeneration'),
  flowStep('enhanceBrandFonts', EnhanceBrandFontsWrapper)
    .withSiderComponent(FontsAutopilotSidebar)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .on('NEXT_FROM_EDIT', 'brandKitConfirm')
    .on('BACK_FROM_EDIT', 'brandKitConfirm')
    .on('NEXT', 'enhanceBrandColors'),

  flowStep('enhanceBrandColors', EnhanceBrandColorsWrapper)
    .on('NEXT_FROM_EDIT', 'brandKitConfirm')
    .on('BACK_FROM_EDIT', 'brandKitConfirm')
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .withSiderComponent(ColorsAutopilotSidebar),

  flowStep('brandKitConfirm', BrandKitConfirmWrapper)
    .withSiderComponent(ConfirmAutopilotSidebar)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .back('profile')
    .on('NAVIGATE_TO_FONTS', 'enhanceBrandFonts')
    .on('NAVIGATE_TO_COLORS', 'enhanceBrandColors')
    .on('BACK_FROM_EDIT', 'brandKitConfirm')
    .on('NEXT', 'campaignGeneration'),

  flowStep('contentGallery', ContentGalleryWrapper)
    .isFullScreen()
    .on('BACK', 'brandKitConfirm')
    .on('BACK_FROM_EDIT', 'brandKitConfirm')
    .on('BACK_TO_PROFILE', 'profile')
    .on('NEXT', 'blazeExplainer'),

  flowStep('blazeExplainer', BlazeExplainerWrapper)
    .back('contentGallery')
    .on('BACK_TO_BRAND_KIT', 'brandKitConfirm')
    .on('NEXT', 'followerGrowthExplainer')
    .isFullWidth()
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),

  flowStep('followerGrowthExplainer', FollowerGrowthExplainerWrapper)
    .back('blazeExplainer')
    .on('NEXT', 'tactics')
    .withSiderComponent(FollowerGrowthAutopilotSidebar)
    .isFullWidth()
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),
]

// ------------------------------------------------- Onboarding Redesign Steps ------------------------------------------------- //
const redesignedSteps = [
  flowStep('screenshotFallback', ScreenshotFallbackWrapper)
    .skipWhen((ctx) => ctx.websiteScrapeSucceeded)
    .back('website'),
  flowStep('noWebsite', NoWebsiteWrapper).back('website'),
  flowStep('profile', BrandProfileStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website', (ctx) => ctx.websiteScrapeSucceeded)
    .back('screenshotFallback', (ctx) => !ctx.websiteScrapeSucceeded)
    .on('IS_MOBILE_NEXT', 'midFlowMobilePushToApp')
    .on('NEXT', 'brandVisualStyleSelection'),
  flowStep('midFlowMobilePushToApp', MidFlowMobilePushToAppWrapper)
    .on('NEXT', 'brandVisualStyleSelection')
    .skipWhen((ctx) => !ctx.isMobile),
  flowStep('brandVisualStyleSelection', BrandVisualStyleSelectionWrapper)
    .back('profile')
    .on('SKIP_VISUAL_STYLES', 'insightChannels')
    .on('NEXT', 'brandVisualStylePreview'),

  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .back('brandVisualStyleSelection')
    .on('NEXT', 'brandVisualStyleOptInOrOut'),
  flowStep('brandVisualStyleOptInOrOut', BrandVisualStyleOptInOrOutWrapper)
    .back('brandVisualStylePreview')
    .on('NEXT_OUT', 'brandVisualStyleMediaOptOut')
    .on('NEXT_IN', 'brandVisualStyleMedia'),

  flowStep('brandVisualStyleMedia', BrandVisualStyleMediaWrapper)
    .back('brandVisualStyleOptInOrOut')
    .on('BACK_TO_PREVIEW', 'brandVisualStylePreview')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .back('brandVisualStyleMedia')
    .on('NEXT', 'brandVisualStyleAnimatedImages'),

  flowStep('brandVisualStyleAnimatedImages', BrandVisualStyleAnimatedImagesWrapper)
    .back('brandVisualStyleFonts')
    .on('NEXT', 'insightChannels'),
  flowStep('brandVisualStyleMediaOptOut', BrandVisualStyleMediaOptOutWrapper)
    .back('brandVisualStyleOptInOrOut')
    .on('NEXT', 'insightChannels'),

  flowStep('insightChannels', InsightChannelsWrapper)
    .back('profile')
    .on('BACK_TO_VISUAL_STYLES_IN', 'brandVisualStyleOptInOrOut')
    .on('BACK_TO_VISUAL_STYLES_OUT', 'brandVisualStyleMediaOptOut')
    .on('NEXT', 'performanceInsights'),

  flowStep('performanceInsights', PerformanceInsightsWrapper)
    .back('insightChannels')
    .on('NEXT', 'strategicRecommendations')
    .on('SKIP', 'whereToPublish'),

  flowStep('strategicRecommendations', StrategicRecommendationsWrapper)
    .back('performanceInsights')
    .on('SKIP', 'whereToPublish')
    .on('NEXT', 'whereToPublish'),

  flowStep('whereToPublish', WhereToPublishWrapper)
    .back('strategicRecommendations')
    .on('BACK_WITHOUT_INSIGHT_CHANNELS', 'performanceInsights')
    .on('BACK_WITHOUT_BRAIN', 'profile')
    .on('BACK_TO_VISUAL_STYLES_IN', 'brandVisualStyleOptInOrOut')
    .on('BACK_TO_VISUAL_STYLES_OUT', 'brandVisualStyleMediaOptOut')
    .on('NEXT_STARTER', 'awareness')
    .on('NEXT_GROWTH', 'campaignDetails'),

  flowStep('campaignDetails', CampaignDetailsWrapper).back('whereToPublish').on('NEXT', 'campaignGenerationLoading'),

  // TODO: REMOVE, NOT USED BUT NEED TO KEEP FOR NOW TO AVOID BREAKING CHANGES
  // -----------------------------------------------------------------
  flowStep('enhanceBrandFonts', EnhanceBrandFontsWrapper)
    .on('NEXT_FROM_EDIT', 'brandKitConfirm')
    .on('BACK_FROM_EDIT', 'brandKitConfirm')
    .on('NEXT', 'enhanceBrandColors'),
  flowStep('enhanceBrandColors', EnhanceBrandColorsWrapper)
    .on('NEXT_FROM_EDIT', 'brandKitConfirm')
    .on('BACK_FROM_EDIT', 'brandKitConfirm'),
  flowStep('brandKitConfirm', BrandKitConfirmWrapper)
    .back('profile')
    .on('NAVIGATE_TO_FONTS', 'enhanceBrandFonts')
    .on('NAVIGATE_TO_COLORS', 'enhanceBrandColors')
    .on('BACK_FROM_EDIT', 'brandKitConfirm')
    .on('NEXT', 'whereToPublish'),
  flowStep('contentGallery', ContentGalleryWrapper)
    .isFullScreen()
    .on('BACK', 'brandKitConfirm')
    .on('BACK_FROM_EDIT', 'brandKitConfirm')
    .on('BACK_TO_PROFILE', 'profile')
    .on('NEXT', 'blazeExplainer'),
  flowStep('blazeExplainer', BlazeExplainerWrapper)
    .back('contentGallery')
    .on('BACK_TO_BRAND_KIT', 'brandKitConfirm')
    .on('NEXT', 'followerGrowthExplainer')
    .isFullWidth(),
  flowStep('followerGrowthExplainer', FollowerGrowthExplainerWrapper)
    .back('blazeExplainer')
    .on('NEXT', 'tactics')
    .isFullWidth(),
  // -----------------------------------------------------------------
]

export const virtualMarketerSteps = defineSteps<FlowCtx>([
  flowStep('website', BrandProfileWebsiteWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NO_WEBSITE', 'noWebsite')
    .on('NEXT', 'profile'),
  ...steps,
])

export const virtualMarketerOnboardingSteps = defineSteps<FlowCtx>([
  flowStep('website', BrandProfileWebsiteWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .back('businessType')
    .on('NO_WEBSITE', 'noWebsite')
    .on('NEXT', 'profile'),
  ...steps,
])

export const evergreenSteps = [
  flowStep('tactics', BrandPlanTacticsWrapper)
    .back('followerGrowthExplainer')
    .on('BACK_TO_BRAND_KIT', 'brandKitConfirm')
    .on('BACK_TO_CONTENT_GALLERY', 'contentGallery')
    .on('BACK_TO_BLAZE_EXPLAINER', 'blazeExplainer')
    .on('BACK_TO_VISUAL_STYLES', 'brandVisualStylePreview')
    .withSiderComponent(TacticsAutopilotSidebar)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),
  flowStep('awareness', BrandPlanAwarenessWrapper)
    .back('whereToPublish')
    .isFullWidth()
    .withSiderComponent(AwarenessAutopilotSidebar)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .on('MOBILE_COMPLETE', 'done'),
  flowStep('currentVolume', BrandPlanCurrentVolumeWrapper)
    .withSiderComponent(CurrentVolumeAutopilotSidebar)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),
]

export const evergreenRedesignSteps = [
  flowStep('tactics', BrandPlanTacticsWrapper)
    .back('whereToPublish')
    .on('BACK_TO_VISUAL_STYLES_IN', 'brandVisualStyleOptInOrOut')
    .on('BACK_TO_VISUAL_STYLES_OUT', 'brandVisualStyleMediaOptOut')
    .on('BACK_TO_BRAND_KIT', 'whereToPublish')
    .on('BACK_TO_CONTENT_GALLERY', 'whereToPublish')
    .on('BACK_TO_BLAZE_EXPLAINER', 'whereToPublish')
    .on('BACK_TO_VISUAL_STYLES', 'brandVisualStylePreview'),
  flowStep('awareness', BrandPlanAwarenessWrapper).back('tactics').on('MOBILE_COMPLETE', 'done'),
  flowStep('currentVolume', BrandPlanCurrentVolumeWrapper),
]

// old current campaign flow
export const campaignGenerationStepsLegacy = [
  flowStep('campaignGeneration', CampaignGenerationWrapper)
    .back('brandKitConfirm')
    .on('BACK_TO_VISUAL_STYLES', 'brandVisualStyleOptInOrOut')
    .on('BACK_TO_VISUAL_STYLES_MEDIA', 'brandVisualStyleMediaOptOut')
    .on('SKIP_CAMPAIGN_GENERATION', 'contentGallery')
    .on('NEXT', 'whereToPublish')
    .withSiderComponent(CampaignGenerationAutopilotSidebar)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground),
  flowStep('whereToPublish', WhereToPublishWrapper)
    .backgroundClassName(AutopilotRegistrationAndOnboardingStyles.cloudBackground)
    .withSiderComponent(TacticsAutopilotSidebar)
    .on('NEXT', 'campaignGenerationLoading')
    .on('NEXT_STARTER', 'awareness')
    .on('NEXT_GROWTH', 'campaignGenerationLoading'),
  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'whereToPublish')
    .on('COMPLETE', 'done'),
]

// split pages for redesign
export const campaignGenerationStepsRedesign = [
  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'campaignDetails')
    .on('COMPLETE', 'done'),
]

export const virtualMarketerRedesignSteps = defineSteps<FlowCtx>([
  flowStep('website', BrandProfileWebsiteWrapper)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NO_WEBSITE', 'noWebsite')
    .on('NEXT', 'profile'),
  ...redesignedSteps,
])

export const campaignGenerationSteps = campaignGenerationStepsRedesign
